import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../../../redux/store";
import {
  coveringOrganizationSelected,
  privateBookingChosen,
} from "../../../../../redux/slice/bookingCreation/bookingCreationSlice";
import { selectBookPrivately, useCoveringOrganizations } from "../../hooks/useCoveredBooking";

/** The option value for "Privat buchen – ich zahle selbst". Never an organization id. */
export const PRIVATE_BOOKING_OPTION = "__private__";

/**
 * Who pays for this booking: one of the customer's organizations that cover
 * the room (#1926), or the customer themselves (PO, 2026-10-20 —
 * docs/plans/member-private-booking.md).
 *
 * A person can hold approved memberships in several organizations, and any
 * member may book a covered room for a private reason. So the control shows
 * as soon as ONE organization covers the room: its options are those
 * organizations plus "Privat buchen – ich zahle selbst".
 *
 * Rendered by the parent only while the booking is ELIGIBLE for coverage
 * (`useIsCoverageEligible`), not while it IS covered — after "privat" the
 * booking is no longer covered, and the member must still be able to switch
 * back. It sits before the personal data, because choosing "privat" changes
 * which fields are required.
 */
const CoveringOrganizationSelect: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const organizations = useCoveringOrganizations();
  const selectedId = useSelector(
    (state: RootState) => state.bookingCreation.orderData.coveringOrganizationId
  );
  const bookPrivately = useSelector(selectBookPrivately);
  const roomId = useSelector(
    (state: RootState) => state.apartment?.selectedApartment?.id
  );

  const hasOrganization = organizations.length > 0;

  // Default to the first organization so the booking is always attributable —
  // an unanswered select among several would silently drop coverage. With one
  // organization the server attributes it itself. "Privat" is never a default:
  // every booking starts on the organization, the main use.
  useEffect(() => {
    if (organizations.length < 2 || bookPrivately) return;
    const stillHeld = organizations.some(
      (organization) => organization.id === selectedId
    );
    if (!stillHeld) {
      dispatch(
        coveringOrganizationSelected({ organizationId: organizations[0].id })
      );
    }
  }, [organizations, selectedId, bookPrivately, dispatch]);

  if (!hasOrganization) return null;

  const value = bookPrivately
    ? PRIVATE_BOOKING_OPTION
    : selectedId ?? organizations[0].id;

  const handleChange = (next: string) => {
    if (next === PRIVATE_BOOKING_OPTION) {
      if (roomId) dispatch(privateBookingChosen({ roomId }));
    } else {
      dispatch(coveringOrganizationSelected({ organizationId: next }));
    }
  };

  return (
    <div className="space-y-1">
      <label
        htmlFor="covering-organization"
        className="text-sm font-medium text-foreground"
      >
        {t("book_location.booking_summary.covering_organization")}
      </label>
      <select
        id="covering-organization"
        className="w-full rounded-[5px] border border-foreground/20 bg-transparent px-3 py-2 text-sm text-foreground"
        value={value}
        onChange={(event) => handleChange(event.target.value)}
      >
        {organizations.map((organization) => (
          <option key={organization.id} value={organization.id}>
            {organization.name}
          </option>
        ))}
        <option value={PRIVATE_BOOKING_OPTION}>
          {t("book_location.booking_summary.book_privately")}
        </option>
      </select>
      <p className="text-sm text-foreground/60">
        {t("book_location.booking_summary.covering_organization_hint")}
      </p>
    </div>
  );
};

export default CoveringOrganizationSelect;
